import React, { useRef, useState } from "react";
import { api, imageUrl } from "@/lib/api";
import { toast } from "sonner";
import { Camera, ImagePlus, X, Star } from "lucide-react";
import { resizeImage } from "@/lib/imageResize";

/**
 * Multi-image uploader. `paths` is the current array; calls onChange(newPaths).
 * Treats paths[0] as the primary thumbnail.
 *
 * Two-button layout: **📷 Take photo** always forces the device camera
 * (single shot via `capture="environment"`), **🖼 Gallery** opens the
 * regular file picker for multi-select. Every uploaded photo is
 * client-resized (≤ 1600 px, JPEG q≈0.85) before being POSTed so the
 * iPad camera's 8 MB raw shots don't hammer object storage.
 */
const MultiImageUpload = ({ paths = [], onChange, onPreview }) => {
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (files) => {
    if (!files?.length) return;
    setBusy(true);
    try {
      const next = [...paths];
      for (const raw of files) {
        const f = await resizeImage(raw);
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push(data.path);
      }
      onChange?.(next);
      toast.success(
        `Uploaded ${files.length} photo${files.length > 1 ? "s" : ""}`,
      );
    } catch (e) {
      toast.error("Upload failed");
    } finally {
      setBusy(false);
      if (cameraRef.current) cameraRef.current.value = "";
      if (galleryRef.current) galleryRef.current.value = "";
    }
  };

  const remove = (p) => onChange?.(paths.filter((x) => x !== p));
  const setPrimary = (p) => onChange?.([p, ...paths.filter((x) => x !== p)]);

  return (
    <div className="w-full">
      {paths.length > 0 && (
        <div className="grid grid-cols-3 gap-2 mb-3">
          {paths.map((p, i) => (
            <div
              key={p}
              className="relative aspect-square border-2 border-black rounded-lg overflow-hidden group"
              data-testid={`stock-photo-${i}`}
            >
              <img
                src={imageUrl(p)}
                alt=""
                className="w-full h-full object-cover cursor-zoom-in"
                onClick={() => onPreview?.(i)}
              />
              {i === 0 && (
                <div className="absolute top-1 left-1 bg-[#228EBF] text-white text-[10px] tracking-wide font-bold px-1.5 py-0.5 rounded flex items-center gap-1">
                  <Star size={10} strokeWidth={2.5} /> Main
                </div>
              )}
              <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                {i !== 0 && (
                  <button
                    type="button"
                    data-testid={`stock-photo-make-primary-${i}`}
                    onClick={() => setPrimary(p)}
                    className="bg-white border-2 border-black rounded p-1"
                    title="Set as primary"
                  >
                    <Star size={12} strokeWidth={2.5} />
                  </button>
                )}
                <button
                  type="button"
                  data-testid={`stock-photo-remove-${i}`}
                  onClick={() => remove(p)}
                  className="bg-[#E5263F] text-white border-2 border-black rounded p-1"
                  title="Remove"
                >
                  <X size={12} strokeWidth={2.5} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Two big touch-friendly buttons — the camera button is the
          primary "yellow" one because on the shop floor the camera is
          how staff will 99% of the time add a photo. Gallery is the
          fallback for pre-existing files. */}
      <div className="grid grid-cols-2 gap-2 mb-2">
        <button
          type="button"
          data-testid="stock-photo-take"
          onClick={() => cameraRef.current?.click()}
          disabled={busy}
          className="flex items-center justify-center gap-2 border-2 border-black rounded-lg py-3 px-3 font-bold text-sm bg-[#FFF7D6] hover:bg-[#FFE9A6] disabled:opacity-60"
        >
          <Camera size={20} strokeWidth={2.5} />
          <span>{busy ? "Uploading…" : "Take photo"}</span>
        </button>
        <button
          type="button"
          data-testid="stock-photo-gallery"
          onClick={() => galleryRef.current?.click()}
          disabled={busy}
          className="flex items-center justify-center gap-2 border-2 border-black rounded-lg py-3 px-3 font-bold text-sm bg-white hover:bg-[#DFEEF7] disabled:opacity-60"
        >
          <ImagePlus size={20} strokeWidth={2.5} />
          <span>Gallery</span>
        </button>
      </div>

      {/* Camera input — single-shot, forces rear camera. iOS Safari
          only opens the camera when `capture` is present AND `multiple`
          is absent, so we split into two inputs. */}
      <input
        ref={cameraRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        data-testid="stock-image-camera-input"
        onChange={(e) => upload(Array.from(e.target.files || []))}
      />
      {/* Gallery input — multi-select, no capture attribute so it opens
          the file/photo picker instead of the camera. */}
      <input
        ref={galleryRef}
        type="file"
        accept="image/*"
        multiple
        hidden
        data-testid="stock-image-gallery-input"
        onChange={(e) => upload(Array.from(e.target.files || []))}
      />
      <div className="text-[11px] tracking-wide font-medium text-[#607087] flex items-center gap-1.5">
        <Camera size={12} strokeWidth={2.5} /> Photos are resized before
        upload · tap to zoom · star to set primary
      </div>
    </div>
  );
};

export default MultiImageUpload;
